import { api } from "@/lib/api";
import { queryKeys } from "@/lib/query";
import { formatDateTime } from "@/utils/datetime";
import { useQueries } from "@tanstack/vue-query";
import { defineStore } from "pinia";
import type { ComputedRef, Ref } from "vue";
import { computed, ref, unref } from "vue";
import type { RouteLocationNormalized } from "vue-router";

export type Breadcrumb = { label: string; route: string };

type DynamicSegment<T = unknown> = {
  route: string;
  /**
   * Must be the entity's canonical query key (queryKeys.*) with a queryFn returning the
   * full entity, so breadcrumbs share the cache with views/prefetch instead of
   * re-fetching the same data under a breadcrumb-only key. `toLabel` shapes the label.
   */
  queryKey: readonly unknown[];
  queryFn: () => Promise<T>;
  toLabel: (data: T) => string;
};

type BreadcrumbSegment = { route: string; staticLabel: string } | DynamicSegment;

// Erases the entity type so differently-typed segments fit one list; pairing of
// queryFn/toLabel stays type-checked at the call site.
const dynamicSegment = <T>(segment: DynamicSegment<T>): DynamicSegment => segment as DynamicSegment;

/**
 * The trail starts at the sidebar section the page belongs to, not at Home: the rail's
 * entries are siblings, so "Home › Playground" claimed a nesting that does not exist. A
 * top-level page is therefore one crumb naming its section — the eyebrow the design draws.
 *
 * Only Home genuinely nests: it *is* the project list, so a project hangs off it.
 */
const getBreadcrumbSegmentsForRoute = (route: RouteLocationNormalized): BreadcrumbSegment[] => {
  const path = route.path;
  const params = route.params as Record<string, string | undefined>;
  const p = params.project_id;
  const s = params.suite_id;
  const c = params.case_id;
  const cycleId = params.cycle_id;
  const batchId = params.batch_id;
  const scheduleId = params.schedule_id;
  const round = params.round;

  const home: BreadcrumbSegment = { route: "/", staticLabel: "Home" };

  if (path === "/") return [home];

  if (path === "/devices") return [{ route: "/devices", staticLabel: "Devices" }];

  if (path === "/artifacts") return [{ route: "/artifacts", staticLabel: "Artifacts" }];

  if (path.startsWith("/settings")) {
    const segments: BreadcrumbSegment[] = [{ route: "/settings", staticLabel: "Settings" }];
    if (path === "/settings/edit") segments.push({ route: "/settings/edit", staticLabel: "Edit Profile" });
    return segments;
  }

  // Global views: they sit above any project, so the trail stops here. The
  // project, when one is picked, rides along in ?project_id= rather than the path.
  if (path === "/playground" || path.startsWith("/playground/")) {
    const segments: BreadcrumbSegment[] = [{ route: "/playground", staticLabel: "Playground" }];
    // A run is identified by its schedule; the header of the run screen carries
    // the human label (artifact name), so the crumb stays with the id.
    if (scheduleId) segments.push({ route: `/playground/${scheduleId}`, staticLabel: `Run #${scheduleId}` });
    return segments;
  }

  if (path === "/runs") return [{ route: "/runs", staticLabel: "Runs" }];

  if (path === "/schedules") return [{ route: "/schedules", staticLabel: "Schedules" }];

  if (path === "/analytics") return [{ route: "/analytics", staticLabel: "Analytics" }];

  if (!p) return [home];

  // A batch is opened from the global Runs list, so that is its section — and the
  // crumb leads back to the whole list, not to it pre-filtered by this batch's
  // project. Narrowing the list has one deliberate way in, the "Run history"
  // button on a project or a suite (N7AIT-1159). The project still gets its own
  // crumb below, because Runs spans every project.
  const root: BreadcrumbSegment = path.includes("/batches") ? { route: "/runs", staticLabel: "Runs" } : home;

  const segments: BreadcrumbSegment[] = [root];
  segments.push(
    // Shares the cache with useProjectName (same key + name-string payload).
    dynamicSegment({
      route: `/${p}`,
      queryKey: [...queryKeys.project(p), "name"] as const,
      queryFn: async () => {
        const res = await api.projects.read(p);
        if (!res.ok) throw new Error(res.error);
        return res.data.name;
      },
      toLabel: (name) => name,
    }),
  );

  // Settings is a drawer over the project page, so the trail stays the project's.
  if (path === `/${p}` || path === `/${p}/settings`) return segments;

  if (path.includes("/batches")) {
    if (batchId) {
      segments.push(
        dynamicSegment({
          route: `/${p}/batches/${batchId}`,
          queryKey: queryKeys.batch(batchId),
          queryFn: async () => {
            const res = await api.schedules.read(batchId);
            if (!res.ok) throw new Error(res.error);
            return res.data;
          },
          toLabel: (d) => d.name || formatDateTime((d.schedule_time ?? d.created_at).date),
        }),
      );
      if (path === `/${p}/batches/${batchId}`) return segments;

      if (cycleId) {
        segments.push(
          dynamicSegment({
            route: `/${p}/batches/${batchId}/${cycleId}`,
            queryKey: queryKeys.cycle(cycleId),
            queryFn: async () => {
              const res = await api.cycles.read(cycleId);
              if (!res.ok) throw new Error(res.error);
              return res.data;
            },
            toLabel: (d) => `Run from ${formatDateTime((d.started_processing_at ?? d.created_at).date)}`,
          }),
        );
        if (round) {
          segments.push({
            route: `/${p}/batches/${batchId}/${cycleId}/${round}`,
            staticLabel: `Step ${round}`,
          });
        }
      }
    }
    return segments;
  }

  // `/:project_id/playground` and `/:project_id/analysis` are router redirects to
  // the global views, so no trail is ever built for them.

  // No "Tests" crumb: it pointed at `/${p}`, the same place as the project crumb
  // right before it, so the trail offered the same destination twice.
  if (!s) return segments;

  segments.push(
    dynamicSegment({
      route: `/${p}/${s}`,
      queryKey: queryKeys.suite(s),
      queryFn: async () => {
        const res = await api.suites.read(s);
        if (!res.ok) throw new Error(res.error);
        return res.data;
      },
      toLabel: (d) => d.name,
    }),
  );
  if (path === `/${p}/${s}`) return segments;

  if (!c) return segments;

  segments.push(
    dynamicSegment({
      route: `/${p}/${s}/${c}`,
      queryKey: queryKeys.case(c),
      queryFn: async () => {
        const res = await api.cases.read(c);
        if (!res.ok) throw new Error(res.error);
        return res.data;
      },
      toLabel: (d) => d.name,
    }),
  );
  if (path === `/${p}/${s}/${c}`) return segments;

  if (cycleId) {
    segments.push(
      dynamicSegment({
        route: `/${p}/${s}/${c}/${cycleId}`,
        queryKey: queryKeys.cycle(cycleId),
        queryFn: async () => {
          const res = await api.cycles.read(cycleId);
          if (!res.ok) throw new Error(res.error);
          return res.data;
        },
        toLabel: (d) => `Run from ${formatDateTime((d.started_processing_at ?? d.created_at).date)}`,
      }),
    );
    if (round)
      segments.push({
        route: `/${p}/${s}/${c}/${cycleId}/${round}`,
        staticLabel: `Step ${round}`,
      });
  }

  return segments;
};

/**
 * Breadcrumbs only. The per-project nav links this store used to expose
 * (projectLink / batchLink / playgroundLink / analysisLink) went away when the
 * side rail became global — the sidebar now builds its own links and carries
 * the project as ?project_id=.
 */
type NavigationTreeStore = {
  currentRoute: Ref<RouteLocationNormalized | null>;
  setRoute: (route: RouteLocationNormalized) => void;
  segments: ComputedRef<BreadcrumbSegment[]>;
};

export const useNavigationTree = defineStore("navigationTree", (): NavigationTreeStore => {
  const currentRoute = ref<RouteLocationNormalized | null>(null);

  const setRoute = (route: RouteLocationNormalized) => {
    currentRoute.value = route;
  };

  const segments = computed(() => (currentRoute.value ? getBreadcrumbSegmentsForRoute(currentRoute.value) : []));

  return {
    currentRoute,
    setRoute,
    segments,
  };
});

export const useBreadcrumbs = () => {
  const store = useNavigationTree();
  const segments = computed(() => store.segments);

  const queries = useQueries({
    queries: computed(() =>
      segments.value.map((seg, i) => {
        if ("staticLabel" in seg)
          return {
            queryKey: ["breadcrumb", "static", i, seg.route] as const,
            queryFn: async () => seg.staticLabel,
          };
        return {
          queryKey: seg.queryKey,
          queryFn: seg.queryFn,
          select: seg.toLabel,
        };
      }),
    ),
  });

  const breadcrumbs = computed<Breadcrumb[]>(() =>
    segments.value.map((seg, i) => {
      const q = queries.value[i];
      const label =
        "staticLabel" in seg
          ? seg.staticLabel
          : (unref(q?.data) ?? (q?.isLoading ? "..." : (seg.route.split("/").pop() ?? "...")));
      return { label: String(label), route: seg.route };
    }),
  );

  return { breadcrumbs };
};
